import { ref } from "vue";

import { useDeleteBorrowerPlatformTemplateFileMutation } from "@/hooks/queries/useBorrowerPlatformTemplates";

interface QueuedDeletion {
  templateId: string;
  fileId: string;
}

const deletionQueue = ref<QueuedDeletion[]>([]);

export const useQueuedFileDeletion = () => {
  const deleteFileMutation = useDeleteBorrowerPlatformTemplateFileMutation();

  const queueFileDeletion = (templateId: string, fileId: string) => {
    const existingIndex = deletionQueue.value.findIndex(
      (item) => item.templateId === templateId && item.fileId === fileId
    );

    if (existingIndex === -1) {
      deletionQueue.value.push({ templateId, fileId });
    }
  };

  const removeFromQueue = (templateId: string, fileId: string) => {
    const index = deletionQueue.value.findIndex(
      (item) => item.templateId === templateId && item.fileId === fileId
    );

    if (index > -1) {
      deletionQueue.value.splice(index, 1);
    }
  };

  const processQueuedDeletions = async () => {
    const deletionsToProcess = [...deletionQueue.value];

    if (deletionsToProcess.length === 0) {
      return;
    }
    await Promise.allSettled(
      deletionsToProcess.map(({ templateId, fileId }) =>
        deleteFileMutation.mutateAsync({ templateId, fileId })
      )
    );
    deletionQueue.value = [];
  };

  const clearQueue = () => {
    deletionQueue.value = [];
  };

  const isFileQueued = (templateId: string, fileId: string): boolean => {
    return deletionQueue.value.some(
      (item) => item.templateId === templateId && item.fileId === fileId
    );
  };

  return {
    queueFileDeletion,
    removeFromQueue,
    processQueuedDeletions,
    clearQueue,
    isFileQueued,
    queuedDeletions: deletionQueue,
  };
};
